<template>
  <div class="d-flex vw-100 vh-100 justify-content-center align-items-center">
    <div class="col-sm-12 col-md-10 col-lg-8 col-xl-6">
      <Brand :width="120" :height="120" />
      <div class="card rounded-custom shadow border-0 p-4">
        <template v-if="isLoading">
          <div class="d-flex justify-content-center align-items-center py-5">
            <div class="spinner-border text-violet" role="status">
              <span class="visually-hidden">{{ $t('Caricamento...') }}</span>
            </div>
          </div>
        </template>

        <template v-else-if="fetchError && !request">
          <div class="alert alert-warning">
            <p class="mb-0">{{ $t('Impossibile caricare i dettagli della richiesta.') }}</p>
            <p class="mb-3 mt-2 small">{{ $t('Puoi comunque provare ad approvare o rifiutare la richiesta.') }}</p>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-violet" :disabled="isApproving || isRejecting" @click="handleApprove">
                <span v-if="isApproving">{{ $t('Approvazione...') }}</span>
                <span v-else>{{ $t('Approva') }}</span>
              </button>
              <button type="button" class="btn btn-outline-secondary" :disabled="isApproving || isRejecting" @click="handleReject">
                <span v-if="isRejecting">{{ $t('Rifiuto...') }}</span>
                <span v-else>{{ $t('Rifiuta') }}</span>
              </button>
            </div>
          </div>
        </template>

        <template v-else-if="successMessage">
          <div class="alert alert-success">
            <p class="mb-0">{{ successMessage }}</p>
            <router-link to="/manager" class="btn btn-violet mt-3">{{ $t('Torna alla dashboard') }}</router-link>
          </div>
        </template>

        <template v-else-if="request">
          <h4 class="mb-4 fw-normal text-center">{{ $t('Richiesta cambio data etichetta') }}</h4>

          <div class="request-details mb-4">
            <div class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Numero prescrizione') }}:</div>
              <div class="col-7">{{ request.prescription_number || '-' }}</div>
            </div>
            <div class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Data etichetta attuale') }}:</div>
              <div class="col-7">{{ formatDate(request.current_delivery_date) }}</div>
            </div>
            <div class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Nuova data etichetta richiesta') }}:</div>
              <div class="col-7 fw-bold">{{ formatDate(request.requested_delivery_date) }}</div>
            </div>
            <div v-if="request.appointment_date" class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Data appuntamento') }}:</div>
              <div class="col-7">{{ formatDate(request.appointment_date) }}</div>
            </div>
            <div class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Motivazione') }}:</div>
              <div class="col-7">{{ request.feedback_description || '-' }}</div>
            </div>
            <div class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Stampa etichetta') }}:</div>
              <div class="col-7">{{ request.print_label ? $t('Sì') : $t('No') }}</div>
            </div>
            <div v-if="request.print_label && request.printer_department" class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Dipartimento stampante') }}:</div>
              <div class="col-7">{{ request.printer_department }}</div>
            </div>
            <div class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Richiesta da') }}:</div>
              <div class="col-7">{{ request.requested_by_name || '-' }}</div>
            </div>
            <div class="row mb-2">
              <div class="col-5 text-muted">{{ $t('Data richiesta') }}:</div>
              <div class="col-7">{{ formatDate(request.requested_at) }}</div>
            </div>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mb-3">
            {{ errorMessage }}
          </div>

          <div class="d-flex gap-2 justify-content-center">
            <button type="button" class="btn btn-violet" :disabled="isApproving || isRejecting" @click="handleApprove">
              <span v-if="isApproving">{{ $t('Approvazione...') }}</span>
              <span v-else>{{ $t('Approva') }}</span>
            </button>
            <button type="button" class="btn btn-outline-secondary" :disabled="isApproving || isRejecting" @click="handleReject">
              <span v-if="isRejecting">{{ $t('Rifiuto...') }}</span>
              <span v-else>{{ $t('Rifiuta') }}</span>
            </button>
          </div>
        </template>
      </div>

      <div class="mt-3 text-center">
        <router-link to="/manager" class="text-secondary text-decoration-none">{{ $t('Torna alla dashboard') }}</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { success } from '@/use/toast/success';
import { error } from '@/use/toast/error';
import Brand from '@/components/Brand';
import { fetchDeliveryDateChangeRequest } from '@/use/repositories/clinic-deliveries/fetchDeliveryDateChangeRequest';
import { approveDeliveryDateChange } from '@/use/repositories/clinic-deliveries/approveDeliveryDateChange';
import { rejectDeliveryDateChange } from '@/use/repositories/clinic-deliveries/rejectDeliveryDateChange';
import { dateFormatter } from '@/use/utilities/time/dateFormatter';

export default {
  name: 'ApproveDeliveryDateChange',
  components: {
    Brand,
  },
  setup() {
    const route = useRoute();
    const i18n = useI18n();

    const requestId = computed(() => route.params.id);
    const request = ref(null);
    const isLoading = ref(true);
    const fetchError = ref(false);
    const isApproving = ref(false);
    const isRejecting = ref(false);
    const successMessage = ref(null);
    const errorMessage = ref(null);

    const formatDate = (date) => {
      if (!date) return '-';
      try {
        return dateFormatter(date, { onlyDate: false, onlyTime: false, dash: true, iso: false });
      } catch {
        return date;
      }
    };

    const loadRequest = async () => {
      isLoading.value = true;
      fetchError.value = false;
      errorMessage.value = null;

      try {
        const data = await fetchDeliveryDateChangeRequest(requestId.value);
        request.value = {
          prescription_number: data.prescription_number ?? data.prescription_number_text ?? data.number_text,
          current_delivery_date: data.current_delivery_date ?? data.delivery_date,
          requested_delivery_date: data.requested_delivery_date ?? data.new_delivery_date ?? data.delivery_date,
          appointment_date: data.appointment_date,
          feedback_description: data.feedback_description ?? data.motivation,
          print_label: data.print_label ?? false,
          printer_department: data.printer_department,
          requested_by_name: data.requested_by_name ?? (data.requested_by ? `${data.requested_by.first_name || ''} ${data.requested_by.last_name || ''}`.trim() : null),
          requested_at: data.requested_at,
          ...data,
        };
      } catch (err) {
        console.error('Errore nel caricamento della richiesta:', err);
        fetchError.value = true;
        request.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const handleApprove = async () => {
      isApproving.value = true;
      errorMessage.value = null;

      try {
        await approveDeliveryDateChange(requestId.value);
        success(i18n.t('Richiesta approvata con successo'));
        successMessage.value = i18n.t('Richiesta approvata con successo');
      } catch (err) {
        const errors = err.response?.data?.errors;
        const message = Array.isArray(errors) ? errors.join(', ') : (errors || err.message || i18n.t("Errore nell'approvazione"));
        errorMessage.value = message;
        error(message);
      } finally {
        isApproving.value = false;
      }
    };

    const handleReject = async () => {
      isRejecting.value = true;
      errorMessage.value = null;

      try {
        await rejectDeliveryDateChange(requestId.value);
        success(i18n.t('Richiesta rifiutata'));
        successMessage.value = i18n.t('Richiesta rifiutata');
      } catch (err) {
        if (err.response?.status === 404) {
          errorMessage.value = i18n.t('Il rifiuto non è ancora implementato dal backend.');
          error(i18n.t('Il rifiuto non è ancora implementato dal backend.'));
        } else {
          const errors = err.response?.data?.errors;
          const message = Array.isArray(errors) ? errors.join(', ') : (errors || err.message || i18n.t("Errore nel rifiuto"));
          errorMessage.value = message;
          error(message);
        }
      } finally {
        isRejecting.value = false;
      }
    };

    onMounted(() => {
      loadRequest();
    });

    return {
      request,
      isLoading,
      fetchError,
      isApproving,
      isRejecting,
      successMessage,
      errorMessage,
      formatDate,
      handleApprove,
      handleReject,
    };
  },
};
</script>

<style scoped>
.request-details .row {
  min-height: 1.5rem;
}
</style>
